import { Graphics, Rectangle, Texture } from "pixi.js"

/**
 * HOW A SPENT ARROW STANDS IN THE GROUND (combat/spent-arrows), shared by a lasting arrow
 * (BasicSprite.poseLandedArrow) and a broken one's stub (effects/broken_arrow.ts), so the two can't
 * drift apart. The arrow art points +x, fletching on the left and the head on the right.
 */

// How much of the arrow art's right-hand end is its head, hidden underground when it stands in the turf.
export const ARROW_HEAD_PX = 6

// The ground is seen from nearly above, so a height is drawn this much shorter than the same length lying on
// the ground. At 1 an arrow on an east-west line would stay full length however upright it stood.
// Tune live from the console: window.arrowHeightDraw = 0.45 (arrows posed after it is set pick it up).
const HEIGHT_DRAW = 0.45
const heightDraw = (): number => {
    const live = typeof window !== "undefined" ? (window as any).arrowHeightDraw : undefined
    return typeof live === "number" && Number.isFinite(live) && live >= 0 ? live : HEIGHT_DRAW
}

/**
 * The on-screen line of a shaft standing in the ground at `pitchDegrees`, its buried end on the ground
 * point, having flown along `heading`. The shaft leans back along the flight and up out of the turf:
 * (-cos h cos p, -sin h cos p - k sin p), height drawn at k. `rotation` turns art pointing +x (toward the
 * buried end) down that line; `lengthScale` shortens it the more upright it stands, never past 1.
 */
export const standingShaft = (heading: number, pitchDegrees: number): { rotation: number; lengthScale: number } => {
    const pitch = Math.max(0, Math.min(90, Number.isFinite(pitchDegrees) ? pitchDegrees : 0)) * Math.PI / 180
    const vx = -Math.cos(heading) * Math.cos(pitch)
    const vy = -Math.sin(heading) * Math.cos(pitch) - heightDraw() * Math.sin(pitch)
    const length = Math.hypot(vx, vy)
    return { rotation: length > 1e-6 ? Math.atan2(-vy, -vx) : -Math.PI / 2, lengthScale: Math.min(1, length) }
}

// Sub-rectangles of an arrow's art, one set per source texture and span: every arrow shares them.
const pieces = new WeakMap<Texture, Map<string, Texture | undefined>>()

/**
 * The art between `fromPx` and `toPx` along the arrow (0 is the fletching end). Undefined for a rotated or
 * trimmed atlas frame, where a plain sub-rectangle doesn't map onto the art.
 */
export const arrowPiece = (texture: Texture, fromPx: number, toPx: number): Texture | undefined => {
    let spans = pieces.get(texture)
    if (!spans) pieces.set(texture, spans = new Map())
    const key = `${fromPx}:${toPx}`
    if (spans.has(key)) return spans.get(key)
    const { frame } = texture
    const from = Math.max(0, Math.round(fromPx)), to = Math.min(frame.width, Math.round(toPx))
    const piece = texture.rotate || texture.trim || to - from < 2 ? undefined
        : new Texture(texture.baseTexture, new Rectangle(frame.x + from, frame.y, to - from, frame.height))
    spans.set(key, piece)
    return piece
}

/** The arrow without its head: what shows of one standing in the ground. */
export const arrowCut = (texture: Texture): Texture | undefined =>
    texture.frame.width <= ARROW_HEAD_PX * 2 ? undefined : arrowPiece(texture, 0, texture.frame.width - ARROW_HEAD_PX)

// The turned earth at a stuck arrow's entry point, which also hides where the art is cut.
const ARROW_TUFT = { dark: 0x3e2c1e, light: 0x7d5f42, alpha: 0.85 }
export const arrowTuft = (): Graphics => {
    const tuft = new Graphics()
    tuft.beginFill(ARROW_TUFT.dark, ARROW_TUFT.alpha).drawEllipse(0, 0.5, 2.5, 1.1).endFill()
    tuft.beginFill(ARROW_TUFT.light, ARROW_TUFT.alpha).drawRect(-2, -0.5, 1, 1).drawRect(1.5, 0, 1, 1).endFill()
    return tuft
}
